import { useRef, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import _ from 'lodash';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    LinearProgress,
} from '@material-ui/core';
import { Cloud as CloudIcon } from '@material-ui/icons';

import { useDetailStyles } from './CertificateStyles';
import { useDataService } from '../../../services/ApiDataService';

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

export default function CertificateImportDialog(props) {
    const { open, setOpen } = props;

    const [file, setFile] = useState(null);
    const [fileSizeError, setFileSizeError] = useState(false);

    const [importing, setImporting] = useState(false);
    const [uploaded, setUploaded] = useState(false);
    const [importMessage, setImportMessage] = useState('');
    const [importErrors, setImportErrors] = useState([]);
    const classes = useDetailStyles();

    const importIdRef = useRef(null);
    const dataService = useDataService();

    const handleFileChange = (e) => {
        setFileSizeError(false);
        const zipFile = e.target.files?.[0];
        if (!zipFile) return;
        setFile(zipFile);
    };

    const getImportInfo = async () => {
        const data = await dataService.get('/ipm/v1/agentCertsImport/importStatus', { id: importIdRef.current });
        setImportMessage(`${data.status}: ${data.message}`);
        if (data.status === 'Completed' || data.status === 'Failed') {
            if (_.isArray(data.errorLog)) {
                setImportErrors(data.errorLog ?? []);
            }
            setImporting(false);
        }
    };
    const handleUpload = async () => {
        if (file.size > MAX_FILE_SIZE) {
            setFileSizeError(true);
            return;
        }
        const importId = Date.now();
        importIdRef.current = importId;
        setUploaded(true);
        setImporting(true);
        setImportMessage('Import in progress');

        const formData = new FormData();
        formData.append('file', file);

        await dataService.postFormData('/ipm/v1/agentCertsImport/importCertificate', formData, {
            statusTimeStamp: importId,
        });
        await getImportInfo();
    };

    const handleCancel = () => {
        setOpen(false);
    };

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Trans>Deploy Certificates</Trans>
            </DialogTitle>
            <DialogContent>
                {!uploaded && (
                    <>
                        <Box display="flex" alignItems="center" gridGap={16} className="file-upload">
                            <Button
                                variant="contained"
                                color="primary"
                                component="label"
                                startIcon={<CloudIcon />}
                                title={'Browse'}
                            >
                                {t`BROWSE`}
                                <input
                                    type="file"
                                    accept=".zip"
                                    id="certificate-zip-input"
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                />
                            </Button>
                            <Box className={classes.dashedBox}>{file?.name || ''}</Box>
                        </Box>
                        <div style={{ fontWeight: '500' }}>
                            <div>
                                <Trans>Choose the .zip file containing the certificate(s).</Trans>{' '}
                            </div>
                            {fileSizeError && (
                                <div style={{ color: '#E66026' }}>
                                    <Trans>Selected file is too large, zip file up to 20M is supported.</Trans>
                                </div>
                            )}
                        </div>
                    </>
                )}
                {uploaded && (
                    <div className={classes.uploadFeedback}>
                        <Box mt={1}>
                            {importing && <LinearProgress />}
                            <Typography variant="body2">{importMessage}</Typography>
                        </Box>

                        {importErrors.length > 0 && (
                            <Box mt={1}>
                                {importErrors.map((err) => (
                                    <Typography key={err} variant="body2">
                                        {err}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                {!importErrors.length && (
                    <Button color="primary" variant="contained" onClick={handleUpload} disabled={!file || importing}>
                        UPLOAD
                    </Button>
                )}
                <Button onClick={handleCancel} disabled={importing}>
                    CLOSE
                </Button>
            </DialogActions>
        </Dialog>
    );
}
